/**
 * Shared UI primitives for game screens.
 * Uses Peppar CSS variables (--primary, --text-primary, etc.) instead of
 * hardcoded prototype theme tokens.
 */
import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'ember';
}

export const PrimaryButton: React.FC<ButtonProps> = ({ children, variant = 'primary', style, ...rest }) => (
  <button
    style={{
      display: 'block',
      width: '100%',
      padding: '14px 0',
      borderRadius: 12,
      border: 'none',
      cursor: rest.disabled ? 'not-allowed' : 'pointer',
      fontFamily: 'Manrope, sans-serif',
      fontWeight: 700,
      fontSize: 14,
      letterSpacing: 1,
      opacity: rest.disabled ? 0.55 : 1,
      background: variant === 'ember'
        ? '#D9472B'
        : 'var(--primary, #F5C21A)',
      color: variant === 'ember'
        ? '#FFFFFF'
        : 'var(--text-on-primary, #1F2428)',
      boxShadow: '0 2px 8px rgba(0,0,0,.10)',
      transition: 'opacity 150ms',
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

export const SecondaryButton: React.FC<ButtonProps> = ({ children, style, ...rest }) => (
  <button
    style={{
      display: 'block',
      width: '100%',
      padding: '13px 0',
      borderRadius: 12,
      border: '1.5px solid var(--border-color, #E5E2D8)',
      cursor: rest.disabled ? 'not-allowed' : 'pointer',
      fontFamily: 'Manrope, sans-serif',
      fontWeight: 600,
      fontSize: 14,
      letterSpacing: 0.5,
      opacity: rest.disabled ? 0.55 : 1,
      background: '#FFFFFF',
      color: 'var(--text-primary, #1F2428)',
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

interface CardProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const Card: React.FC<CardProps> = ({ children, style }) => (
  <div
    style={{
      background: '#FFFFFF',
      border: '1px solid var(--border-color, #E5E2D8)',
      borderRadius: 14,
      padding: '18px 16px',
      boxShadow: '0 1px 6px rgba(0,0,0,.06)',
      ...style,
    }}
  >
    {children}
  </div>
);

interface PillProps {
  children: React.ReactNode;
  tone?: 'gold' | 'muted';
}

export const Pill: React.FC<PillProps> = ({ children, tone = 'gold' }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 600,
      fontFamily: 'Manrope, sans-serif',
      background: tone === 'gold' ? 'var(--primary, #F5C21A)' : 'var(--bg-subtle, #F7F5EF)',
      color: tone === 'gold' ? 'var(--text-on-primary, #1F2428)' : 'var(--text-muted, #9B8F6E)',
    }}
  >
    {children}
  </span>
);

interface ScreenHeaderProps {
  title: React.ReactNode;
  subtitle?: string;
  onClose?: () => void;
}

export const ScreenHeader: React.FC<ScreenHeaderProps> = ({ title, subtitle, onClose }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      padding: '20px 20px 14px',
      borderBottom: '1px solid var(--border-color, #E5E2D8)',
    }}
  >
    <div>
      <div
        style={{
          fontFamily: 'Manrope, sans-serif',
          fontWeight: 800,
          fontSize: 20,
          color: 'var(--text-primary, #1F2428)',
          lineHeight: 1.25,
        }}
      >
        {title}
      </div>
      {subtitle && (
        <div
          style={{
            fontSize: 12.5,
            color: 'var(--text-secondary, #6B5E3E)',
            marginTop: 4,
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
    {onClose && (
      <button
        onClick={onClose}
        style={{
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          fontSize: 20,
          color: 'var(--text-muted, #9B8F6E)',
          padding: '0 0 0 12px',
          lineHeight: 1,
          marginTop: 2,
        }}
        aria-label="Close"
      >
        ✕
      </button>
    )}
  </div>
);
